import { useGridRootProps } from "@components/DataGrid/hooks/useGridRootProps";
import { GridRowVisibilityCheckboxParams } from "@components/DataGrid/models/gridRowVisibilityCheckboxParams";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { SvgIcon } from "@mui/material";
import { unstable_composeClasses as composeClasses, unstable_useForkRef as useForkRef } from "@mui/utils";
import { getDataGridUtilityClass, GridRenderCellParams, useGridApiContext, useGridSelector } from "@mui/x-data-grid-premium";
import { DataGridPremiumProcessedProps } from "@mui/x-data-grid-premium/models/dataGridPremiumProps";
import PropTypes from "prop-types";
import { ChangeEvent, forwardRef, KeyboardEvent, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { getVisibilityPropsSelector } from "./utils";

export type OwnerState = { classes: DataGridPremiumProcessedProps["classes"] };

const useUtilityClasses = (ownerState: OwnerState) => {
  const { classes } = ownerState;

  const slots = {
    root: ["checkboxInput"],
  };

  return composeClasses(slots, getDataGridUtilityClass, classes);
};

interface TouchRippleActions {
  stop: (event: any, callback?: () => void) => void;
}

const GridCellVisibilityCheckboxForwardRef = forwardRef<HTMLInputElement, GridRenderCellParams>((props, ref) => {
  const { field, id, formattedValue, row, rowNode, colDef, isEditable, cellMode, hasFocus, tabIndex, api, ...other } = props;
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const ownerState = { classes: rootProps.classes };
  const classes = useUtilityClasses(ownerState);
  const checkboxElement = useRef<HTMLElement>(null);

  const rippleRef = useRef<TouchRippleActions>(null);
  const handleRef = useForkRef(checkboxElement, ref);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const params: GridRowVisibilityCheckboxParams = { value: event.target.checked, id };
    apiRef.current.publishEvent("rowVisibilityCheckboxChange" as any, params, event);
  };

  useLayoutEffect(() => {
    if (tabIndex === 0) {
      const element = apiRef.current.getCellElement(id, field);
      if (element) {
        element.tabIndex = -1;
      }
    }
  }, [apiRef, tabIndex, id, field]);

  useEffect(() => {
    if (hasFocus) {
      const input = checkboxElement.current?.querySelector("input");
      input?.focus({ preventScroll: true });
    } else if (rippleRef.current) {
      // Only available in @mui/material v5.4.1 or later
      rippleRef.current.stop({});
    }
  }, [hasFocus]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === " ") {
      // We call event.stopPropagation to avoid selecting the row and also scrolling to bottom
      // TODO: Remove and add a check inside useGridKeyboardNavigation
      event.stopPropagation();
    }
  }, []);

  const isSelectable = apiRef.current.isRowSelectable(id);

  const visibilityPropsSelector = getVisibilityPropsSelector(id, rootProps.rowVisibilitySelectionPropagation?.parents ?? false);
  const { isIndeterminate, isChecked } = useGridSelector(apiRef, visibilityPropsSelector);

  if (rowNode.type === "footer" || rowNode.type === "pinnedRow") {
    return null;
  }

  const label = apiRef.current.getLocaleText(isChecked ? "visibilitySelectionUnselectRow" : ("visibilitySelectionSelectRow" as any));

  const checked = rootProps.indeterminateVisibilityAction === "select" ? isChecked && !isIndeterminate : isChecked;

  return (
    <rootProps.slots.baseCheckbox
      ref={handleRef}
      tabIndex={tabIndex}
      checked={checked}
      onChange={handleChange}
      className={classes.root}
      inputProps={{ "aria-label": label }}
      onKeyDown={handleKeyDown}
      indeterminate={isIndeterminate}
      disabled={!isSelectable}
      touchRippleRef={rippleRef as any /* FIXME: typing error */}
      icon={<VisibilityOffIcon fontSize="inherit" />}
      checkedIcon={<VisibilityIcon fontSize="inherit" sx={{ color: "text.primary" }} />}
      indeterminateIcon={
        <SvgIcon fontSize="inherit" style={{ color: "text.secondary" }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="40 -800 880 600" width="22px" height="15px" fill={"var(--text-color-secondary)"}>
            <path d="M 480 -200 C 431.334 -200 384.834 -206.792 340.5 -220.375 C 296.167 -233.959 254 -254.334 214 -281.5 C 174 -308.666 139.5 -340.458 110.5 -376.875 C 81.5 -413.292 58 -454.333 40 -500 C 58 -545.666 81.5 -586.708 110.5 -623.125 C 139.5 -659.542 174 -691.333 214 -718.5 C 254 -745.666 296.167 -766.041 340.5 -779.625 C 384.833 -793.208 431.333 -800 480 -800 C 528.667 -800 575.167 -793.208 619.5 -779.625 C 663.833 -766.041 706 -745.666 746 -718.5 C 786 -691.333 820.5 -659.542 849.5 -623.125 C 878.5 -586.708 902 -545.666 920 -500 C 902 -454.333 878.5 -413.292 849.5 -376.875 C 820.5 -340.458 786 -308.666 746 -281.5 C 706 -254.334 663.833 -233.959 619.5 -220.375 C 575.167 -206.792 528.667 -200 480 -200 M 480 -280 C 517.667 -280 553.792 -284.958 588.375 -294.875 C 622.958 -304.792 656 -319.667 687.5 -339.5 C 719 -359.333 746.792 -382.625 770.875 -409.375 C 794.959 -436.125 815.334 -466.333 832 -500 C 815.334 -533.666 794.959 -563.875 770.875 -590.625 C 746.792 -617.375 719 -640.666 687.5 -660.5 C 656 -680.333 622.958 -695.208 588.375 -705.125 C 553.792 -715.042 517.667 -720 480 -720 C 442.334 -720 406.209 -715.042 371.625 -705.125 C 337.042 -695.208 304 -680.333 272.5 -660.5 C 241 -640.666 213.208 -617.375 189.125 -590.625 C 165.041 -563.875 144.666 -533.666 128 -500 M 128 -500 C 144.666 -466.333 165.041 -436.125 189.125 -409.375 C 213.208 -382.625 241 -359.333 272.5 -339.5 C 304 -319.666 337.042 -304.791 371.625 -294.875 C 406.208 -284.958 442.333 -280 480 -280" />
            <rect x="117.98" y="-549" width="728" height="94" />
          </svg>
        </SvgIcon>
      }
      {...rootProps.slotProps?.baseCheckbox}
      {...other}
    />
  );
});

GridCellVisibilityCheckboxForwardRef.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * GridApi that let you manipulate the grid.
   */
  api: PropTypes.object.isRequired,
  /**
   * The mode of the cell.
   */
  cellMode: PropTypes.oneOf(["edit", "view"]).isRequired,
  /**
   * The column of the row that the current cell belongs to.
   */
  colDef: PropTypes.object.isRequired,
  /**
   * The column field of the cell that triggered the event.
   */
  field: PropTypes.string.isRequired,
  /**
   * A ref allowing to set imperative focus.
   * It can be passed to the element that should receive focus.
   * @ignore - do not document.
   */
  focusElementRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.shape({
        focus: PropTypes.func.isRequired,
      }),
    }),
  ]),
  /**
   * The cell value formatted with the column valueFormatter.
   */
  formattedValue: PropTypes.any,
  /**
   * If true, the cell is the active element.
   */
  hasFocus: PropTypes.bool.isRequired,
  /**
   * The grid row id.
   */
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  /**
   * If true, the cell is editable.
   */
  isEditable: PropTypes.bool,
  /**
   * The row model of the row that the current cell belongs to.
   */
  row: PropTypes.any.isRequired,
  /**
   * The node of the row that the current cell belongs to.
   */
  rowNode: PropTypes.object.isRequired,
  /**
   * the tabIndex value.
   */
  tabIndex: PropTypes.oneOf([-1, 0]).isRequired,
  /**
   * The cell value.
   * If the column has `valueGetter`, use `params.row` to directly access the fields.
   */
  value: PropTypes.any,
} as any;

export { GridCellVisibilityCheckboxForwardRef };

export const GridCellVisibilityCheckboxRenderer = GridCellVisibilityCheckboxForwardRef;
